import { useState } from 'react';
import { ExternalLink, Image as ImageIcon, Plus } from 'lucide-react';
import { useCms } from './CmsContext';
import type { Litter } from '../../lib/api';
import { MediaModal } from '../editor/MediaModal';

const STATUSES = ['available', 'planned', 'reserved'] as const;

export function Litters() {
  const { litters, setLitters, resetLitters } = useCms();
  const [mediaFor, setMediaFor] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const update = (id: string, patch: Partial<Litter>) => setLitters((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  const remove = (id: string) => {
    setLitters((items) => items.filter((item) => item.id !== id));
    setDeleteId(null);
  };
  const add = () => setLitters((items) => [...items, { id: crypto.randomUUID(), title: 'New pairing', description: '', expectedDate: '', spotsLeft: 0, status: 'planned', imageUrl: null, imageCrop: null }]);
  const activeMediaLitter = litters.find((litter) => litter.id === mediaFor);

  return (
    <div className="cms-page">
      <div className="cms-page-head cms-page-head-row">
        <div>
          <h1>Litters</h1>
          <p>Manage the pairings, availability, dates, and imagery families see across your site.</p>
        </div>
        <div className="cms-home-actions">
          <button type="button" className="cms-btn cms-btn-ghost" onClick={() => window.confirm('Replace the current litter draft with the Sugarloaf defaults?') && resetLitters()}>Reset to defaults</button>
          <button type="button" className="cms-btn cms-btn-primary" onClick={add}><Plus size={15} />Add litter</button>
        </div>
      </div>

      <div className="cms-cards">
        {litters.map((litter) => (
          <article key={litter.id} className="cms-card">
            <header className="cms-litter-head">
              {litter.imageUrl ? <img className="cms-litter-image" src={litter.imageUrl} alt="" /> : <span className="cms-litter-image cms-litter-image-empty"><ImageIcon size={20} /></span>}
              <div className="cms-litter-title">
                <h2>{litter.title || 'Untitled pairing'}</h2>
                <span className={`cms-status cms-status-${litter.status}`}>{litter.status}</span>
              </div>
              <div className="cms-litter-actions">
                <a className="cms-btn cms-btn-ghost" href={`/litters/${encodeURIComponent(litter.id)}`} target="_blank" rel="noreferrer">Preview <ExternalLink size={13} /></a>
                <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setMediaFor(litter.id)}><ImageIcon size={14} />Choose image</button>
              </div>
            </header>

            <label className="cms-field"><span>Pairing / title</span><input value={litter.title} onChange={(event) => update(litter.id, { title: event.target.value })} /></label>
            <label className="cms-field"><span>Description</span><textarea rows={3} value={litter.description} onChange={(event) => update(litter.id, { description: event.target.value })} /></label>
            <div className="cms-field-row">
              <label className="cms-field"><span>Public status</span>
                <select value={litter.status} onChange={(event) => update(litter.id, { status: event.target.value as Litter['status'] })}>
                  {STATUSES.map((status) => <option key={status} value={status}>{status[0].toUpperCase() + status.slice(1)}</option>)}
                </select>
              </label>
              <label className="cms-field"><span>Expected date</span><input type="date" value={litter.expectedDate} onChange={(event) => update(litter.id, { expectedDate: event.target.value })} /></label>
              <label className="cms-field"><span>Spots left</span><input type="number" min={0} value={litter.spotsLeft} onChange={(event) => update(litter.id, { spotsLeft: Math.max(0, Number(event.target.value)) })} /></label>
            </div>
            <label className="cms-field"><span>Cover image URL</span><input type="url" value={litter.imageUrl ?? ''} placeholder="https://…" onChange={(event) => update(litter.id, { imageUrl: event.target.value || null, imageCrop: null })} /></label>

            {deleteId === litter.id ? (
              <div className="cms-delete-confirm" role="alert">
                <span><b>Delete {litter.title || 'this litter'}?</b><small>It will be removed from the public site after you save.</small></span>
                <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
                <button type="button" className="cms-btn cms-btn-danger" onClick={() => remove(litter.id)}>Delete litter</button>
              </div>
            ) : <button type="button" className="cms-btn cms-btn-danger" onClick={() => setDeleteId(litter.id)}>Delete litter</button>}
          </article>
        ))}
      </div>

      {!litters.length && (
        <section className="cms-card cms-litters-empty">
          <DogEmpty />
          <h2>No litter records</h2>
          <p>Add a planned or available pairing to bring the litters section back to life.</p>
          <button type="button" className="cms-btn cms-btn-primary" onClick={add}><Plus size={15} />Add your first litter</button>
        </section>
      )}

      <MediaModal
        open={Boolean(activeMediaLitter)}
        onClose={() => setMediaFor(null)}
        onSelect={(url) => { if (activeMediaLitter) update(activeMediaLitter.id, { imageUrl: url, imageCrop: null }); }}
      />
    </div>
  );
}

function DogEmpty() {
  return <span className="cms-empty-icon" aria-hidden="true"><ImageIcon size={22} /></span>;
}
